import { useState } from "react";
import { Pressable } from "react-native";
import { useSetAtom } from "jotai";
import { GripVerticalIcon, XIcon } from "lucide-react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from "react-native-reanimated";

import { ApplicationTypeEnumType } from "@libs/api/patients";
import { VnBox } from "@/components/VnBox";
import { VnIcon } from "@/components/VnIcon";
import { VnText } from "@/components/VnText/VnText";
import { ListedApplicationType } from "@/domain/applications";
import {
  DashboardDraftCountsType,
  DashboardInputsType,
} from "@/domain/dashboardCards";
import { HouseholdType } from "@/domain/household";
import {
  RedeterminationStatusType,
  RedeterminationType,
} from "@/domain/redeterminations";
import { WorkRequirementType } from "@/domain/workRequirements";
import { dashboardScenarioAtom } from "@/stores/atoms";
import { cn } from "@/utils/tailwind";

/**
 * NON-PROD-only floating panel to drive the dashboard
 * from any of the prototype's 6×9 scenario cells without seeding real data.
 * It sets `dashboardScenarioAtom`, which `useDashboardCards` reads in place of
 * the live queries. Draggable by its header; collapsible to a chip.
 *
 * Labels are intentionally hardcoded English — this tool never ships to
 * production, so it is exempt from i18n.
 */

const NO_DRAFTS: DashboardDraftCountsType = {
  medicaid: 0,
  medicare: 0,
  marketplace: 0,
};

const household = (
  eligibilityLastStep: HouseholdType["eligibilityLastStep"],
): HouseholdType => ({ eligibilityLastStep }) as HouseholdType;

const submittedMedicaid = (): ListedApplicationType =>
  ({
    applicationType: "MEDICAID",
    brands: [],
  }) as unknown as ListedApplicationType;

// Needs a real id and applicant names: the confirmation card is keyed by id and
// its copy names the programs and the people applied for.
const justSubmittedApplication = (
  applicationType: ApplicationTypeEnumType,
  applicantFirstNames: string[],
): ListedApplicationType =>
  ({
    id: `scenario-application-${applicationType}`,
    applicationType,
    applicants: applicantFirstNames.map((firstName, index) => ({
      householdMemberId: `scenario-member-${firstName}-${index}`,
      firstName,
    })),
    brands: [],
  }) as unknown as ListedApplicationType;

const renewal = (
  status: RedeterminationStatusType,
  index: number,
): RedeterminationType =>
  ({
    id: `scenario-renewal-${status}-${index}`,
    status,
    redeterminationDate: "2026-09-15",
  }) as RedeterminationType;

type JourneyPresetType = {
  key: string;
  label: string;
  build: () => Pick<
    DashboardInputsType,
    "household" | "submittedApplications" | "draftCounts"
  > &
    Partial<Pick<DashboardInputsType, "submissionConfirmationApplicationIds">>;
};

const JOURNEY_PRESETS: JourneyPresetType[] = [
  {
    key: "eligibility_not_started",
    label: "Eligibility not started",
    build: () => ({ household: household(null), draftCounts: NO_DRAFTS }),
  },
  {
    key: "eligibility_in_progress",
    label: "Eligibility in progress",
    build: () => ({
      household: household("MARITAL_STATUS"),
      draftCounts: NO_DRAFTS,
    }),
  },
  {
    key: "ready_to_apply",
    label: "Ready to apply",
    build: () => ({ household: household("FINISHED"), draftCounts: NO_DRAFTS }),
  },
  {
    key: "application_in_progress",
    label: "Application in progress",
    build: () => ({
      household: household("FINISHED"),
      draftCounts: { ...NO_DRAFTS, medicaid: 1 },
    }),
  },
  {
    key: "submitted",
    label: "Submitted applications",
    build: () => ({
      household: household("FINISHED"),
      submittedApplications: [submittedMedicaid()],
      draftCounts: NO_DRAFTS,
    }),
  },
  {
    key: "submitted_draft",
    label: "Submitted + draft",
    build: () => ({
      household: household("FINISHED"),
      submittedApplications: [submittedMedicaid()],
      draftCounts: { ...NO_DRAFTS, medicaid: 1 },
    }),
  },
  {
    key: "just_submitted",
    label: "Just submitted",
    build: () => {
      const applications = [
        justSubmittedApplication("MEDICAID", ["Daniel", "Maria"]),
        justSubmittedApplication("MEDICARE", ["Daniel"]),
      ];

      return {
        household: household("FINISHED"),
        submittedApplications: applications,
        draftCounts: NO_DRAFTS,
        submissionConfirmationApplicationIds: applications.map(
          application => application.id,
        ),
      };
    },
  },
];

type RenewalPresetKeyType =
  | "NONE"
  | "START"
  | "FORM"
  | "CALL"
  | "TWO_FORM"
  | "TWO_CALL"
  | "FORM_CALL"
  | "START_FORM"
  | "COMPLETED";

type RenewalPresetType = {
  key: RenewalPresetKeyType;
  label: string;
  build: () => RedeterminationType[];
};

const RENEWAL_PRESETS: RenewalPresetType[] = [
  { key: "NONE", label: "No renewals", build: () => [] },
  {
    key: "START",
    label: "Start renewal",
    build: () => [renewal("NOT_STARTED", 1)],
  },
  {
    key: "FORM",
    label: "Waiting for form",
    build: () => [renewal("PENDING_FORM", 1)],
  },
  {
    key: "CALL",
    label: "Waiting for call",
    build: () => [renewal("CALL_BACK", 1)],
  },
  {
    key: "TWO_FORM",
    label: "Two waiting for form",
    build: () => [renewal("PENDING_FORM", 1), renewal("PENDING_FORM", 2)],
  },
  {
    key: "TWO_CALL",
    label: "Two waiting for call",
    build: () => [renewal("CALL_BACK", 1), renewal("CALL_BACK", 2)],
  },
  {
    key: "FORM_CALL",
    label: "Form + call",
    build: () => [renewal("PENDING_FORM", 1), renewal("CALL_BACK", 1)],
  },
  {
    key: "START_FORM",
    label: "Start + waiting for form",
    build: () => [renewal("NOT_STARTED", 1), renewal("PENDING_FORM", 1)],
  },
  {
    key: "COMPLETED",
    label: "Completed renewal",
    build: () => [renewal("COMPLETED", 1)],
  },
];

const workRequirement = ({
  screeningStatus,
  progress = "ACTION_NEEDED",
}: Pick<WorkRequirementType, "screeningStatus"> &
  Partial<Pick<WorkRequirementType, "progress">>): WorkRequirementType =>
  ({
    id: `scenario-work-requirement-${screeningStatus}-${progress}`,
    workRequirementDate: "2027-01-01",
    screeningStatus,
    screeningLastStep: screeningStatus === "IN_PROGRESS" ? "WHO" : null,
    screenedAt:
      screeningStatus === "COMPLETED" ? "2026-09-23T00:00:00.000Z" : null,
    progress,
    members: [],
  }) as unknown as WorkRequirementType;

type WorkRequirementPresetType = {
  key: string;
  label: string;
  build: () => WorkRequirementType[];
};

// Mirrors the design prototype's work requirement scenarios. They drive the
// dashboard card and section; the linked screens load real data.
const WORK_REQUIREMENT_PRESETS: WorkRequirementPresetType[] = [
  { key: "NONE", label: "No work requirements", build: () => [] },
  {
    key: "START",
    label: "Start",
    build: () => [workRequirement({ screeningStatus: "NOT_STARTED" })],
  },
  {
    key: "CONTINUE",
    label: "Continue",
    build: () => [workRequirement({ screeningStatus: "IN_PROGRESS" })],
  },
  {
    key: "UPLOAD",
    label: "Upload documents / month unlocked",
    build: () => [workRequirement({ screeningStatus: "COMPLETED" })],
  },
  {
    key: "UP_TO_DATE",
    label: "Up to date (months locked)",
    build: () => [
      workRequirement({ screeningStatus: "COMPLETED", progress: "UP_TO_DATE" }),
    ],
  },
  {
    key: "COMPLETED",
    label: "All documents uploaded",
    build: () => [
      workRequirement({ screeningStatus: "COMPLETED", progress: "COMPLETED" }),
    ],
  },
];

const Chip = ({
  label,
  isSelected,
  onPress,
}: {
  label: string;
  isSelected: boolean;
  onPress: () => void;
}) => (
  <Pressable
    accessibilityRole="button"
    onPress={onPress}
    className={cn(
      "rounded-full border px-3 py-1",
      isSelected ? "bg-primary border-primary" : "bg-card border-black/15",
    )}>
    <VnText
      variant="sm"
      className={isSelected ? "text-primary-foreground" : "text-foreground"}>
      {label}
    </VnText>
  </Pressable>
);

export const DashboardScenarioPanel = () => {
  const setScenario = useSetAtom(dashboardScenarioAtom);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [journeyKey, setJourneyKey] = useState<string | null>(null);
  const [renewalKey, setRenewalKey] = useState<string | null>(null);
  const [workRequirementKey, setWorkRequirementKey] = useState<string | null>(
    null,
  );

  const translateX = useSharedValue(16);
  const translateY = useSharedValue(90);
  const startOffset = useSharedValue({ x: 16, y: 90 });

  const panGesture = Gesture.Pan()
    .onStart(() => {
      startOffset.value = { x: translateX.value, y: translateY.value };
    })
    .onUpdate(event => {
      translateX.value = startOffset.value.x + event.translationX;
      translateY.value = startOffset.value.y + event.translationY;
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
    ],
  }));

  const applyScenario = ({
    nextJourneyKey,
    nextRenewalKey,
    nextWorkRequirementKey,
  }: {
    nextJourneyKey: string;
    nextRenewalKey: string;
    nextWorkRequirementKey: string;
  }) => {
    const journey = JOURNEY_PRESETS.find(
      preset => preset.key === nextJourneyKey,
    )!;
    const renewals = RENEWAL_PRESETS.find(
      preset => preset.key === nextRenewalKey,
    )!;
    const workRequirements = WORK_REQUIREMENT_PRESETS.find(
      preset => preset.key === nextWorkRequirementKey,
    )!;

    setScenario({
      submissionConfirmationApplicationIds: [],
      ...journey.build(),
      redeterminations: renewals.build(),
      workRequirements: workRequirements.build(),
      dismissedCardIds: [],
    });
  };

  const handleSelectJourney = (key: string) => {
    setJourneyKey(key);
    applyScenario({
      nextJourneyKey: key,
      nextRenewalKey: renewalKey ?? "NONE",
      nextWorkRequirementKey: workRequirementKey ?? "NONE",
    });
  };

  const handleSelectRenewal = (key: string) => {
    setRenewalKey(key);
    applyScenario({
      nextJourneyKey: journeyKey ?? "ready_to_apply",
      nextRenewalKey: key,
      nextWorkRequirementKey: workRequirementKey ?? "NONE",
    });
  };

  const handleSelectWorkRequirement = (key: string) => {
    setWorkRequirementKey(key);
    applyScenario({
      nextJourneyKey: journeyKey ?? "submitted",
      nextRenewalKey: renewalKey ?? "NONE",
      nextWorkRequirementKey: key,
    });
  };

  const handleReset = () => {
    setJourneyKey(null);
    setRenewalKey(null);
    setWorkRequirementKey(null);
    setScenario(null);
  };

  return (
    <Animated.View
      style={[
        { position: "absolute", top: 0, left: 0, zIndex: 50, maxWidth: 340 },
        animatedStyle,
      ]}>
      <VnBox className="border-primary/40 bg-card rounded-lg border p-3 shadow-lg">
        <GestureDetector gesture={panGesture}>
          <VnBox className="flex flex-row items-center justify-between gap-2">
            <VnBox className="flex flex-row items-center gap-1">
              <VnIcon as={GripVerticalIcon} size={16} />
              <VnText variant="mdMedium">🧪 Dashboard scenarios</VnText>
            </VnBox>
            <Pressable
              accessibilityRole="button"
              onPress={() => setIsCollapsed(previous => !previous)}>
              <VnText variant="smMedium">{isCollapsed ? "＋" : "－"}</VnText>
            </Pressable>
          </VnBox>
        </GestureDetector>

        {!isCollapsed && (
          <VnBox className="mt-2 gap-3">
            <VnBox className="gap-1">
              <VnText variant="smMedium" className="text-muted-foreground">
                Journey
              </VnText>
              <VnBox className="flex flex-row flex-wrap gap-1">
                {JOURNEY_PRESETS.map(preset => (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    isSelected={journeyKey === preset.key}
                    onPress={() => handleSelectJourney(preset.key)}
                  />
                ))}
              </VnBox>
            </VnBox>

            <VnBox className="gap-1">
              <VnText variant="smMedium" className="text-muted-foreground">
                Renewals
              </VnText>
              <VnBox className="flex flex-row flex-wrap gap-1">
                {RENEWAL_PRESETS.map(preset => (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    isSelected={renewalKey === preset.key}
                    onPress={() => handleSelectRenewal(preset.key)}
                  />
                ))}
              </VnBox>
            </VnBox>

            <VnBox className="gap-1">
              <VnText variant="smMedium" className="text-muted-foreground">
                Work requirements
              </VnText>
              <VnBox className="flex flex-row flex-wrap gap-1">
                {WORK_REQUIREMENT_PRESETS.map(preset => (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    isSelected={workRequirementKey === preset.key}
                    onPress={() => handleSelectWorkRequirement(preset.key)}
                  />
                ))}
              </VnBox>
            </VnBox>

            <Pressable
              accessibilityRole="button"
              onPress={handleReset}
              className="flex flex-row items-center gap-1 self-start">
              <VnIcon as={XIcon} size={14} />
              <VnText variant="sm">Reset to real data</VnText>
            </Pressable>
          </VnBox>
        )}
      </VnBox>
    </Animated.View>
  );
};
